/**
 * Which currency a visitor is shown on the pricing page.
 *
 * The rule deliberately mirrors the app's own: `getCurrencyFromTimezone` in the
 * portal repo (`utils/datetime.ts`) maps these eight US timezones to USD at
 * registration and falls back to EUR for everywhere else. Keeping the same list
 * here is the point — the price on the marketing site is then the price the
 * account is actually billed in, rather than a second, unrelated guess.
 *
 * If that list changes in the portal, change it here too.
 */
export type Currency = 'EUR' | 'USD'

const US_TIMEZONES = new Set([
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Adak',
  'Pacific/Honolulu',
])

const STORAGE_KEY = 'signital.currency'

/**
 * The currency to show before the visitor says otherwise. Browser-only: the
 * pages are prerendered, so the static HTML always carries EUR and this runs
 * after hydration.
 */
export function detectCurrency(): Currency {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return US_TIMEZONES.has(tz) ? 'USD' : 'EUR'
  } catch {
    return 'EUR'
  }
}

/** A currency the visitor picked by hand, which outranks detection. */
export function readStoredCurrency(): Currency | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v === 'EUR' || v === 'USD' ? v : null
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return null
  }
}

export function storeCurrency(currency: Currency): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, currency)
  } catch {
    // Not being able to remember the choice is not a reason to fail the toggle.
  }
}
